import { AlertTriangle, Hourglass, PhoneOutgoing } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { UpdatesSummaryData } from '@/types/orderUpdates';
import { formatUpdateClock, localDigits, pickLang } from './updatesFormat';

interface UpdatesSummaryCardProps {
  summary: UpdatesSummaryData;
  /** The order's own state, shown as the strip above "what happened". */
  status?: string | null;
  statusReason?: string | null;
}

// last_call.status → its pill. Answered is the only good outcome; the rest read as a problem.
const CALL_PILL: Record<string, { key: string; tone: string }> = {
  answered: { key: 'updates_call_status_answered', tone: 'bg-[#DFF1E7] text-[#226B4B]' },
  no_answer: { key: 'updates_call_status_no_answer', tone: 'bg-[#FBE3E3] text-[#B83232]' },
  busy: { key: 'updates_call_status_busy', tone: 'bg-[#FBE3E3] text-[#B83232]' },
};

/**
 * BOXY-715 — the card a merchant reads before anything else: what state the order is in, what
 * happened in one paragraph, and when Boxy last reached the customer.
 *
 * It replaces a counters line ("2 calls · 1 answered · Last: …"), which told a merchant how much
 * contact there had been but never what came of it.
 */
export function UpdatesSummaryCard({ summary, status, statusReason }: UpdatesSummaryCardProps) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'en';
  const text = pickLang(summary.text, lang);
  const pill = summary.last_call ? CALL_PILL[summary.last_call.status] : undefined;

  return (
    <div className='overflow-hidden rounded-2xl border border-solid border-sd-border bg-sd-card'>
      {status ? (
        <div className='flex items-center gap-2 bg-[#F6EEDF] px-4 py-2.5 text-[13px] font-semibold text-[#9A6A12]'>
          <AlertTriangle className='h-4 w-4 shrink-0' strokeWidth={2} aria-hidden />
          <span className='min-w-0 flex-1 truncate'>{status}</span>
          {statusReason ? <span className='font-medium'>{statusReason}</span> : null}
        </div>
      ) : null}

      <div className='flex flex-col gap-2.5 px-4 py-3.5'>
        <h3 className='m-0 text-[12px] font-semibold tracking-wide text-sd-muted-foreground'>
          {String(t('updates_what_happened'))}
        </h3>

        {text ? (
          <p dir='auto' className='m-0 text-start text-[15px] leading-relaxed'>
            {text}
          </p>
        ) : (
          <p className='m-0 flex items-center gap-2 text-[14px] text-sd-muted-foreground'>
            <Hourglass className='h-4 w-4 shrink-0' strokeWidth={2} aria-hidden />
            {String(t('updates_summary_pending'))}
          </p>
        )}

        {summary.last_call ? (
          <p className='m-0 flex flex-wrap items-center gap-2 border-0 border-t border-solid border-sd-border pt-2.5 text-[13px] text-sd-foreground'>
            <PhoneOutgoing className='h-4 w-4 shrink-0 text-sd-muted-foreground' strokeWidth={2} aria-hidden />
            <span>{String(t('updates_last_call_label'))}</span>
            {pill ? (
              <span className={`rounded-full px-2 py-0.5 text-[12px] font-semibold ${pill.tone}`}>
                {String(t(pill.key))}
              </span>
            ) : null}
            <span className='tabular-nums text-sd-muted-foreground'>
              {localDigits(formatUpdateClock(summary.last_call.at, lang), lang)}
            </span>
          </p>
        ) : null}
      </div>
    </div>
  );
}
